import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ROLES } from '../constants/preset-constants';
import { getUserAdvancePermissions } from '../services/advancePermissionService';
import { IAppState } from '../store/types';

/**
 * Returns whether the currently logged-in user has been assigned a specific
 * advanced capability (USER_CAPABILITIES_* settingTypeId).
 *
 * @param capabilityId - one of the USER_CAPABILITIES_* constants
 * @returns true once confirmed, false while loading or if not assigned
 */
const useUserCapability = (capabilityId: number): boolean => {
    const userInfo = useSelector((state: IAppState) => state.userInfo);
    const userId = userInfo?.id;
    const profileId = Number(userInfo?.profileId);
    const inheritsFrom = Number(userInfo?.inheritsFrom);
    const isSystemOwner = profileId === ROLES.ID_SYSTEM_OWNER || inheritsFrom === ROLES.ID_SYSTEM_OWNER;
    const [hasCapability, setHasCapability] = useState(false);

    useEffect(() => {
        // System Owner is always allowed without explicit advanced capability assignment.
        if (isSystemOwner) {
            setHasCapability(true);
            return;
        }

        if (!userId) {
            return;
        }

        let cancelled = false;
        getUserAdvancePermissions(userId)
            .then(({ rows }) => {
                if (!cancelled) {
                    setHasCapability(rows.some(p => p.settingTypeId === capabilityId));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setHasCapability(false);
                }
            });
        return () => { 
            cancelled = true; 
        };
    }, [userId, capabilityId, isSystemOwner]);

    return hasCapability;
};

export default useUserCapability;
